/**
 * Who is selling this.
 *
 * Every legal page, every receipt and the footer read from here, so the seller's
 * identity is stated in exactly one place. Getting it wrong in four places and
 * right in one is how a refund policy ends up naming an entity that never
 * existed.
 *
 * ## The one thing you have to fill in
 *
 * `PENDING` marks a field that cannot be guessed and must not be invented: the
 * legal entity, where it is established, and the address that has to appear on
 * an invoice. Until they are set, the legal pages say plainly that the seller is
 * not yet named rather than printing a placeholder — a terms page naming
 * "Example Ltd" is a worse document than one admitting it is incomplete, because
 * the first is wrong and reads as finished.
 *
 * That is the same rule the rest of the product runs on: nothing ships as a
 * statement of fact unless something backs it. See src/core/answers.ts.
 */

/** A field whose real value is not known yet. Never rendered as if it were. */
export const PENDING = null;

export interface CompanyIdentity {
  /** Trading name. Safe to state today — it is the product's own name. */
  product: string;
  /**
   * The studio that publishes it, and the organization name on Polar.
   *
   * Deliberately not the same field as `legalName`. A Polar organization is a
   * brand with a slug and a checkout page; a legal entity is registered with a
   * government, has a jurisdiction, and is who a customer would actually sue.
   * Collapsing the two would put a name on the terms that no registry has heard
   * of, which is a worse document than one that omits the clause.
   *
   * This is the name a customer sees at checkout and on their statement, so it
   * is the right thing to say in "Vouchkeep is operated by —".
   */
  organization: string;
  /** Registered legal entity that takes the money and signs the terms. */
  legalName: string | null;
  /** Country whose law governs the terms and where the entity is established. */
  jurisdiction: string | null;
  /** Registered address, as it must appear on an invoice. One line per entry. */
  address: string[] | null;
  /** Company/tax registration number, if the jurisdiction issues one. */
  registrationNumber: string | null;
}

export const COMPANY: CompanyIdentity = {
  product: 'Vouchkeep',
  organization: 'Oktobar',

  // TODO(polar): set these three once the entity behind Oktobar is registered.
  // They decide which country's consumer law the refund policy has to satisfy
  // and what the governing law clause can say, so they are not cosmetic — the
  // terms omit that clause entirely rather than guess at it.
  legalName: PENDING,
  jurisdiction: PENDING,
  address: PENDING,

  registrationNumber: PENDING,
};

/**
 * Where customers write, and where they are answered.
 *
 * Polar's account review measures support responsiveness — the commitment on the
 * contact page is 48 hours and it is a real promise, so this has to be an address
 * a human reads rather than a form that files a ticket nobody sees.
 */
export const SUPPORT_EMAIL = 'support@vouchkeep.com';

/** Reports of a vulnerability in Vouchkeep itself. Read faster than support. */
export const SECURITY_EMAIL = 'security@vouchkeep.com';

/** Privacy requests: access, export, deletion. May be the same inbox as support. */
export const PRIVACY_EMAIL = 'privacy@vouchkeep.com';

/**
 * The date the current legal texts took effect.
 *
 * Bumped by hand when a term changes, because a page that dates itself to
 * whenever it was rendered tells a customer nothing about whether the deal
 * changed since they agreed to it.
 */
export const LEGAL_EFFECTIVE_DATE = '2026-08-21';

/**
 * Polar is the merchant of record, not a payment gateway we happen to use.
 *
 * This distinction has to survive into the customer-facing text: Polar is the
 * counterparty on the payment, it charges the card, it is named on the statement,
 * and it is responsible for sales tax and VAT. Saying "we use Polar for payments"
 * would be both wrong and, in the EU, a tax claim we are not entitled to make.
 */
export const MERCHANT_OF_RECORD = {
  name: 'Polar Software Inc.',
  role: 'merchant of record',
  /**
   * What the customer will actually see on their card statement.
   *
   * Polar builds this from the organization name. Confirm it against the first
   * real charge and correct it here if it differs — a descriptor a customer
   * cannot reconcile against something they remember buying is how a
   * subscription becomes a chargeback instead of a support email.
   */
  statementHint: 'POLAR*OKTOBAR',
  url: 'https://polar.sh',
} as const;

/**
 * Everyone who touches customer data, and what they get.
 *
 * The privacy page renders this list verbatim. It is deliberately short, and
 * keeping it short is a product decision rather than an accident — the pitch is
 * that questionnaires are parsed in the browser and the model key is never
 * stored, and every row added here makes that pitch weaker.
 */
export interface Subprocessor {
  name: string;
  purpose: string;
  /** What actually reaches them. Written from the customer's point of view. */
  dataReceived: string;
  location: string;
}

export const SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Cloudflare, Inc.',
    purpose: 'Application hosting, database and document storage',
    dataReceived:
      'Account details, evidence cards, question text, and any documents you upload to your trust page',
    location: 'United States, with edge processing worldwide',
  },
  {
    name: 'Polar Software Inc.',
    purpose: 'Merchant of record — checkout, billing, invoices and tax',
    dataReceived:
      'Billing name, email, billing address, tax identifiers and payment details. Payment card numbers never reach Vouchkeep.',
    location: 'United States',
  },
  {
    name: 'Resend, Inc.',
    purpose: 'Transactional email — password resets, receipts and review reminders',
    dataReceived: 'Your email address and the contents of those messages',
    location: 'United States',
  },
];

/**
 * Whether the *registered entity* is known.
 *
 * Not "whether there is a name to print" — there always is, see `sellerName`.
 * This gates the two clauses that need a registry behind them: the governing
 * law, and the registration number. Everything else can be said about Oktobar
 * today.
 */
export function companyIsNamed(): boolean {
  return COMPANY.legalName !== null && COMPANY.jurisdiction !== null;
}

/**
 * Who to name as the seller, in prose.
 *
 * The registered entity when there is one, the organization otherwise. It never
 * falls back to the product's own name, because "Vouchkeep is operated by
 * Vouchkeep" is not a sentence that tells a customer anything.
 */
export function sellerName(): string {
  return COMPANY.legalName ?? COMPANY.organization;
}
